import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  CheckIcon,
  StarIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon as CheckIconSolid } from "@heroicons/react/24/solid";
import { socialAPI } from "../services/socialAuth";
import LoadingSpinner from "./LoadingSpinner";

const UpgradeModal = ({
  isOpen,
  onClose,
  currentTier = "free",
  accountsUsed,
  accountLimit,
  limitsData,
}) => {
  const [upgradeOptions, setUpgradeOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchUpgradeOptions();
    }
  }, [isOpen]);

  const fetchUpgradeOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await socialAPI.getUpgradeOptions();
      setUpgradeOptions(response.data);
    } catch (err) {
      console.error("Failed to fetch upgrade options:", err);
      setError("Failed to load upgrade options");
      // Use fallback data
      setUpgradeOptions(getFallbackOptions());
    } finally {
      setLoading(false);
    }
  };

  const getFallbackOptions = () => {
    return {
      current_tier: currentTier,
      upgrade_options: [
        {
          tier: "standard",
          name: "Standard",
          monthly_price: 75,
          annual_price: 720,
          limits: {
            max_connections: 10,
            data_retention_days: 90,
            api_calls_per_day: 10000,
          },
          features: [
            "basic_metrics",
            "advanced_metrics",
            "manual_sync",
            "auto_sync",
          ],
          upgrade_url: "/pricing",
        },
        {
          tier: "premium",
          name: "Premium",
          monthly_price: 150,
          annual_price: 1440,
          limits: {
            max_connections: 50,
            data_retention_days: 365,
            api_calls_per_day: 100000,
          },
          features: [
            "basic_metrics",
            "advanced_metrics",
            "manual_sync",
            "auto_sync",
            "custom_reports",
            "api_access",
            "priority_support",
          ],
          upgrade_url: "/pricing",
        },
      ],
    };
  };

  const getFeatureName = (featureKey) => {
    const featureNames = {
      basic_metrics: "Basic analytics dashboard",
      advanced_metrics: "Advanced analytics & insights",
      manual_sync: "Manual data refresh",
      auto_sync: "Automatic data syncing",
      custom_reports: "Custom reports & exports",
      api_access: "API access for integrations",
      priority_support: "Priority email support",
    };
    return featureNames[featureKey] || featureKey;
  };

  const handleUpgrade = (tier) => {
    // Redirect to pricing page or Stripe Checkout
    window.location.href = `/pricing?tier=${tier}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              Upgrade Your Plan
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Connect more social accounts and unlock advanced features
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Current Status Alert */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2"></div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-amber-800">
                  Account Limit Reached
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  You're using {accountsUsed} of {accountLimit} accounts on the{" "}
                  <span className="font-semibold capitalize">
                    {currentTier}
                  </span>{" "}
                  tier. Upgrade to connect more social media accounts and access
                  advanced analytics.
                </p>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">
                Loading upgrade options...
              </span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Upgrade Options */}
          {!loading && upgradeOptions && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upgradeOptions.upgrade_options.map((option) => (
                <div
                  key={option.tier}
                  className={`
                    bg-white rounded-xl border-2 transition-all hover:shadow-lg
                    ${
                      option.tier === "premium"
                        ? "border-purple-500 shadow-md"
                        : "border-gray-200"
                    }
                  `}
                >
                  {option.tier === "premium" && (
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-t-lg flex items-center justify-center gap-2">
                      <StarIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">Most Popular</span>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Plan Name */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {option.name}
                    </h3>

                    {/* Pricing */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-gray-900">
                          ${option.monthly_price}
                        </span>
                        <span className="text-gray-600">/month</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        or ${option.annual_price}/year (save{" "}
                        {Math.round(
                          ((option.monthly_price * 12 - option.annual_price) /
                            (option.monthly_price * 12)) *
                            100,
                        )}
                        %)
                      </div>
                    </div>

                    {/* Limits */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Social Accounts</span>
                        <span className="font-semibold text-gray-900">
                          {option.limits.max_connections} accounts
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Data Retention</span>
                        <span className="font-semibold text-gray-900">
                          {option.limits.data_retention_days} days
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">API Calls</span>
                        <span className="font-semibold text-gray-900">
                          {(
                            option.limits.api_calls_per_day / 1000
                          ).toLocaleString()}
                          K/day
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">
                        Features Included
                      </h4>
                      <ul className="space-y-2">
                        {option.features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-start gap-2 text-sm"
                          >
                            <CheckIconSolid className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">
                              {getFeatureName(feature)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={() => handleUpgrade(option.tier)}
                      className={`
                        w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2
                        ${
                          option.tier === "premium"
                            ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 shadow-md"
                            : "bg-primary-600 text-white hover:bg-primary-700"
                        }
                      `}
                    >
                      Upgrade to {option.name}
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Trust Indicators */}
          {!loading && (
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center">
                  <ShieldCheckIcon className="w-8 h-8 text-blue-600 mb-2" />
                  <span className="text-sm text-blue-800 font-medium">
                    Secure Payment
                  </span>
                  <span className="text-xs text-blue-600 mt-1">
                    Encrypted via Stripe
                  </span>
                </div>
                <div className="flex flex-col items-center">
                  <ClockIcon className="w-8 h-8 text-blue-600 mb-2" />
                  <span className="text-sm text-blue-800 font-medium">
                    Cancel Anytime
                  </span>
                  <span className="text-xs text-blue-600 mt-1">
                    No commitments
                  </span>
                </div>
                <div className="flex flex-col items-center">
                  <CheckIcon className="w-8 h-8 text-blue-600 mb-2" />
                  <span className="text-sm text-blue-800 font-medium">
                    14-Day Trial
                  </span>
                  <span className="text-xs text-blue-600 mt-1">
                    Money-back guarantee
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* View Full Pricing */}
          <div className="mt-6 text-center">
            <button
              onClick={() => (window.location.href = "/pricing")}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              View full pricing details →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
